import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { last, get } from 'global/lib/lodash';
import sendData from 'redux/actions/sendData';
import { LoadingTools } from 'global/components/LoadingIcons';
import { redeemHash } from './redeemHash';
import { PDF_POLL_INTERVAL_MS, PDF_POLL_TIMEOUT_MS, pdfUrlFromMedia } from './messagePdf';

const GENERATING_TEXT = "Generating PDF: You'll be redirected when this PDF is ready.";

const rootSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: 2,
  p: 4,
  textAlign: 'center',
  bgcolor: 'background.default',
};

const absoluteHttpUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.href;
  } catch (err) {
    return null;
  }
};

const tradeTypeIdFromSearch = (search) => {
  const value = new URLSearchParams(search || '').get('tradeTypeId');
  return value || null;
};

export default function MessagePdfPage() {
  const { hash, messageId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const [problem, setProblem] = useState(null);
  const tradeTypeId = tradeTypeIdFromSearch(location.search);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let sawMessage = false;

    const fail = (next) => {
      if (!cancelled) setProblem(next);
    };

    const poll = async (startedAt) => {
      if (cancelled) return;

      try {
        const result = await sendData(
          { url: `messages/${messageId}`, method: 'get' },
          { normalize: { build: ['messages'] }, params: { include: 'media' } }
        );
        sawMessage = true;
        const message = last(result);
        const rawUrl = pdfUrlFromMedia(get(message, 'media'), tradeTypeId);
        const pdfUrl = rawUrl ? absoluteHttpUrl(rawUrl) : null;
        if (pdfUrl) {
          cancelled = true;
          window.location.replace(pdfUrl);
          return;
        }
      } catch (err) {
        const statusCode = get(err, 'response.status');
        if (statusCode === 401 || statusCode === 403 || statusCode === 404) {
          fail('unavailable');
          return;
        }
      }

      if (cancelled) return;
      if (Date.now() - startedAt >= PDF_POLL_TIMEOUT_MS) {
        fail(sawMessage ? 'timeout' : 'failed');
        return;
      }

      timer = setTimeout(() => poll(startedAt), PDF_POLL_INTERVAL_MS);
    };

    setProblem(null);
    dispatch(redeemHash(hash, { kind: 'message', id: messageId }))
      .then(() => {
        if (!cancelled) poll(Date.now());
      })
      .catch(() => {
        fail('unavailable');
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatch, hash, messageId, tradeTypeId]);

  if (problem === 'unavailable') {
    return (
      <Box sx={rootSx} data-testid="message-pdf-page">
        <Typography variant="h5">This link can't be opened</Typography>
        <Typography variant="body1">It may have expired or been revoked.</Typography>
      </Box>
    );
  }

  if (problem === 'failed') {
    return (
      <Box sx={rootSx} data-testid="message-pdf-page">
        <Typography variant="h5">We couldn't check on this PDF</Typography>
        <Typography variant="body1">Refresh the page to try again.</Typography>
      </Box>
    );
  }

  if (problem === 'timeout') {
    return (
      <Box sx={rootSx} data-testid="message-pdf-page">
        <Typography variant="h5">Your PDF is still being prepared</Typography>
        <Typography variant="body1">It will also be emailed to recipients. Refresh the page to check again.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={rootSx} data-testid="message-pdf-page">
      <Typography variant="h5" role="status" sx={{ maxWidth: 520 }}>
        {GENERATING_TEXT}
      </Typography>
      <LoadingTools morph aria-hidden fontSize="large" color="primary" sx={{ fontSize: '6rem' }} />
    </Box>
  );
}
